import { CdsClock } from '@diekeure/components';
import { createInput, createSelect, MountTarget, newDialog, readConfig } from './utils/elements';

interface ClockConfigInterface {
  time?: string;

  secondHand?: boolean;
  minuteHand?: boolean;
  hourHand?: boolean;
  color?: boolean;
  live?: boolean;
  roman?: boolean;

  scale?: number;
  hourLabels?: 'all' | 'quarter' | 'none';
  minuteLabels?: 'all' | 'none';
}

export const KLOK_EDIT_SELECTOR = '.cds-lrn-klok';

export function klokDialog(insertHtml: (html: string) => void, mountTarget?: MountTarget, existingEl?: HTMLElement) {
  const dialog = newDialog('Klok', () => insertHtml(''), mountTarget);

  const time = createInput('time', 'Tijd', 'time');
  time.field.setAttribute('step', '1');

  const handConfig = document.createElement('div');
  handConfig.style.display = 'flex';
  handConfig.style.gap = '2rem';

  const secondHand = createInput('secondHand', 'Secondenwijzer', 'checkbox');
  const minuteHand = createInput('minuteHand', 'Minutenwijzer', 'checkbox');
  const hourHand = createInput('hourHand', 'Urenwijzer', 'checkbox');
  handConfig.append(hourHand.field, minuteHand.field, secondHand.field);

  const styleConfig = document.createElement('div');
  styleConfig.style.display = 'flex';
  styleConfig.style.gap = '2rem';

  const color = createInput('color', 'Kleur', 'checkbox');
  const live = createInput('live', 'Live', 'checkbox');
  const roman = createInput('roman', 'Romeinse cijfers', 'checkbox');

  styleConfig.append(color.field, live.field, roman.field);

  const labelConfig = document.createElement('div');
  labelConfig.style.display = 'flex';
  labelConfig.style.flexDirection = 'column';
  labelConfig.style.gap = '1rem';

  const hourLabels = createSelect('hourLabels', 'Uren labels', [
    { label: 'Alle', value: 'all' },
    { label: '0 | 3 | 6 | 9', value: 'quarter' },
    { label: 'Geen', value: 'none' },
  ]);
  hourLabels.setValue('all');

  const minuteLabels = createSelect('minuteLabels', 'Minuten labels', [
    { label: 'Alle', value: 'all' },
    { label: 'Geen', value: 'none' },
  ]);
  minuteLabels.setValue('none');

  labelConfig.append(hourLabels.field, minuteLabels.field);

  const scale = createInput('scale', 'Grootte (schaal in %)', 'number');
  scale.setValue(70);

  dialog.querySelector('#cds-dialog-content').append(time.field, handConfig, styleConfig, labelConfig, scale.field);

  if (existingEl) {
    const cfg = readConfig<ClockConfigInterface>(existingEl, {});
    if (cfg.time) time.setValue(cfg.time);
    secondHand.setChecked(!!cfg.secondHand);
    minuteHand.setChecked(!!cfg.minuteHand);
    hourHand.setChecked(!!cfg.hourHand);
    color.setChecked(!!cfg.color);
    live.setChecked(!!cfg.live);
    roman.setChecked(!!cfg.roman);
    if (cfg.hourLabels) hourLabels.setValue(cfg.hourLabels);
    if (cfg.minuteLabels) minuteLabels.setValue(cfg.minuteLabels);
    if (cfg.scale) scale.setValue(Math.round(cfg.scale * 100));
  }

  dialog.addEventListener('onSubmit', () => {
    const config: ClockConfigInterface = {
      time: time.value(),
      secondHand: secondHand.checked(),
      minuteHand: minuteHand.checked(),
      hourHand: hourHand.checked(),

      color: color.checked(),
      live: live.checked(),
      roman: roman.checked(),

      scale: +scale.value() / 100,
      hourLabels: hourLabels.value() as 'all' | 'quarter' | 'none',
      minuteLabels: minuteLabels.value() as 'all' | 'none',
    };
    const html = makeClock(config);
    insertHtml(html);
  });
}

function makeClock(config: ClockConfigInterface) {
  const clock: CdsClock = document.createElement('cds-clock') as CdsClock;

  const { width, height } = measureElement(clock);

  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const hours = pad(now.getHours());
  const minutes = pad(now.getMinutes());
  const seconds = pad(now.getSeconds());

  clock.setAttribute('time', config.time || `${hours}:${minutes}:${seconds}`);

  ['secondHand', 'minuteHand', 'hourHand', 'color', 'live', 'roman', 'hourLabels', 'minuteLabels'].forEach((attr) => {
    if (!config[attr]) return;
    clock.setAttribute(attr, config[attr]);
  });

  clock.setAttribute('readonly', '');

  clock.style.transform = `scale(${config.scale || 1})`;

  const wrapper = document.createElement('div');
  wrapper.classList.add('cds-lrn-klok');
  wrapper.setAttribute('data-cb-config', JSON.stringify(config));
  wrapper.style.display = 'inline-flex';
  wrapper.style.alignItems = 'center';
  wrapper.style.justifyContent = 'center';

  wrapper.style.width = `${width * (config.scale || 1)}px`;
  wrapper.style.height = `${height * (config.scale || 1)}px`;

  wrapper.append(clock);

  return wrapper.outerHTML;
}

function measureElement(element) {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.visibility = 'hidden';

  document.body.appendChild(container);

  const clone = element.cloneNode(true);

  container.appendChild(clone);

  const rect = container.getBoundingClientRect();
  const measurements = {
    width: rect.width,
    height: rect.height,
  };

  document.body.removeChild(container);

  return measurements;
}
